import { useAtomValue } from 'jotai';
import { createContext, useMemo, useState } from 'react';

import { useTablePagination } from '@/lib/components/Table/useTablePagination';

import { atomTotalsProcess } from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import {
  IChildProps,
  IFilterByTable,
  IMutationContext,
  IOrderByTable,
  IStateContext,
} from '@/src/modules/CampaignsModule/interfaces/Campaigns';
import { useGetContext } from '@/src/utils/ContextUtils';

const CampaignsStateContext = createContext<IStateContext | undefined>(undefined);
const CampaignsMutationContext = createContext<IMutationContext | undefined>(undefined);

const CampaignsProvider = ({ children }: IChildProps) => {
  const totalsCampaignList = useAtomValue(atomTotalsProcess);
  const [orderBy, setOrderBy] = useState<IOrderByTable>({
    sortField: 'lastUpdate',
    sortOrder: 'desc',
  });
  const [filterBy, setFilterBy] = useState<IFilterByTable>({});
  const [clearFilterBy, setClearFilterBy] = useState<boolean>(false);
  const { pageSize, pageIndex, totalPages, gotoPageIndex, changeTablePage, resetPagination } =
    useTablePagination(totalsCampaignList.partial);

  const memoizedMutations: IMutationContext = useMemo<IMutationContext>(
    () => ({
      changeTablePage,
      resetPagination,
      setOrderBy,
      setFilterBy,
      setClearFilterBy,
    }),
    [changeTablePage, resetPagination, setOrderBy, setFilterBy, setClearFilterBy]
  );

  const state: IStateContext = useMemo<IStateContext>(
    () => ({
      pageSize,
      pageIndex,
      totalPages,
      gotoPageIndex,
      filterBy,
      clearFilterBy,
      orderBy,
    }),
    [pageSize, pageIndex, totalPages, gotoPageIndex, filterBy, clearFilterBy, orderBy]
  );

  return (
    <CampaignsMutationContext.Provider value={memoizedMutations}>
      <CampaignsStateContext.Provider value={state}>{children}</CampaignsStateContext.Provider>
    </CampaignsMutationContext.Provider>
  );
};

export const useCampaignsMutationContext = (): IMutationContext =>
  useGetContext<IMutationContext>(CampaignsMutationContext, 'CampaignsMutationContext');

export const useCampaignsStateContext = (): IStateContext =>
  useGetContext<IStateContext>(CampaignsStateContext, 'CampaignsStateContext');

export default CampaignsProvider;
